<template>
  <!-- ═══════════════ MOBILE ═══════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Themes</h1>
        <button v-if="!loading && !error" @click="toggleModal"
                class="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">
          Preview
        </button>
      </div>
    </div>

    <!-- Mobile loading skeleton -->
    <div v-if="loading" class="pb-20 pt-6 px-4 animate-pulse">
      <div class="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-4 mx-1"></div>
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden">
          <div class="h-28 bg-gray-100 dark:bg-gray-800"></div>
          <div class="px-3 py-2.5 flex items-center gap-2">
            <div class="h-3 w-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-3 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile error state -->
    <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5">{{ error }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg">Try again</button>
    </div>

    <!-- Mobile content -->
    <div v-else class="pb-20 pt-6 px-4">
      <p class="text-xs text-gray-400 dark:text-gray-500 mb-4 px-1">{{ parseLocale(I18N.website_content.account_settings.themes_and_styles_subheading, { application_name: APPLICATION_NAME }) }}</p>

      <div class="grid grid-cols-2 gap-3">
        <!-- Basic theme card -->
        <button type="button" @click="toggleModal"
                class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden border-2 border-blue-500 dark:border-blue-500 shadow-sm text-left">
          <div class="overflow-hidden">
            <img src="/images/card_preview.png" class="w-full object-cover" alt="Basic theme preview" />
          </div>
          <div class="px-3 py-2.5 flex items-center justify-between gap-2">
            <div class="flex items-center gap-1.5 min-w-0">
              <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.993 12c0 2.761 2.246 5.007 5.007 5.007s5.007-2.246 5.007-5.007S14.761 6.993 12 6.993 6.993 9.239 6.993 12zM12 8.993c1.658 0 3.007 1.349 3.007 3.007S13.658 15.007 12 15.007 8.993 13.658 8.993 12 10.342 8.993 12 8.993zM10.998 19h2v3h-2zm0-17h2v3h-2zm-9 9h3v2h-3zm17 0h3v2h-3zM4.219 18.363l2.12-2.122 1.415 1.414-2.12 2.122zM16.24 6.344l2.122-2.122 1.414 1.414-2.122 2.122zM6.342 7.759 4.22 5.637l1.415-1.414 2.12 2.122zm13.434 10.605-1.414 1.414-2.122-2.122 1.414-1.414z"/>
              </svg>
              <span class="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{{ I18N.website_content.account_settings.basic }}</span>
            </div>
            <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 flex-shrink-0">
              {{ I18N.website_content.account_settings.appearance_tab.active }}
            </span>
          </div>
        </button>
      </div>
    </div>
  </div>

  <!-- ═══════════════ DESKTOP ═══════════════ -->
  <div class="hidden sm:block min-h-screen bg-gray-50 dark:bg-[#0d1117]">
    <!-- Sub-page header -->
    <div class="sticky top-14 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-5xl mx-auto px-6 flex items-center h-14">
        <div class="flex items-center gap-2.5">
          <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.1 0 2-.9 2-2 0-.52-.2-1-.5-1.36-.29-.35-.47-.82-.47-1.32 0-1.1.9-2 2-2h2.36c3.1 0 5.61-2.51 5.61-5.61C22.5 6.02 17.74 2 12 2zM6.5 11C5.67 11 5 10.33 5 9.5S5.67 8 6.5 8 8 8.67 8 9.5 7.33 11 6.5 11zm3-4C8.67 7 8 6.33 8 5.5S8.67 4 9.5 4s1.5.67 1.5 1.5S10.33 7 9.5 7zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 4 14.5 4s1.5.67 1.5 1.5S15.33 7 14.5 7zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 8 17.5 8s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">{{ I18N.website_content.account_settings.themes_and_styles }}</h1>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Public Profile</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop loading skeleton -->
    <div v-if="loading" class="max-w-5xl mx-auto px-6 py-6 animate-pulse">
      <div class="h-4 w-80 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div class="bg-white dark:bg-[#1C2128] rounded-xl overflow-hidden">
          <div class="h-32 bg-gray-100 dark:bg-gray-800"></div>
          <div class="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <div class="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop error state -->
    <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5">{{ error }}</p>
      <button @click="retry" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg">Try again</button>
    </div>

    <!-- Desktop content -->
    <div v-else class="max-w-5xl mx-auto px-6 py-6">
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{{ parseLocale(I18N.website_content.account_settings.themes_and_styles_subheading, { application_name: APPLICATION_NAME }) }}</p>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <!-- Basic theme card -->
        <button type="button" @click="toggleModal"
                class="group bg-white dark:bg-[#1C2128] rounded-xl overflow-hidden border-2 border-blue-500 dark:border-blue-500 shadow-sm hover:shadow-md transition-shadow text-left">
          <div class="overflow-hidden">
            <img src="/images/card_preview.png" class="w-full object-cover group-hover:scale-[1.02] transition-transform duration-200" alt="Basic theme preview" />
          </div>
          <div class="px-4 py-3 flex items-center justify-between gap-2 border-t border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2 min-w-0">
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.993 12c0 2.761 2.246 5.007 5.007 5.007s5.007-2.246 5.007-5.007S14.761 6.993 12 6.993 6.993 9.239 6.993 12zM12 8.993c1.658 0 3.007 1.349 3.007 3.007S13.658 15.007 12 15.007 8.993 13.658 8.993 12 10.342 8.993 12 8.993zM10.998 19h2v3h-2zm0-17h2v3h-2zm-9 9h3v2h-3zm17 0h3v2h-3zM4.219 18.363l2.12-2.122 1.415 1.414-2.12 2.122zM16.24 6.344l2.122-2.122 1.414 1.414-2.122 2.122zM6.342 7.759 4.22 5.637l1.415-1.414 2.12 2.122zm13.434 10.605-1.414 1.414-2.122-2.122 1.414-1.414z"/>
              </svg>
              <span class="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{{ I18N.website_content.account_settings.basic }}</span>
            </div>
            <span class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 flex-shrink-0">
              {{ I18N.website_content.account_settings.appearance_tab.active }}
            </span>
          </div>
        </button>
      </div>
    </div>
  </div>

  <!-- Profile preview panel -->
  <div v-show="settingsStore.profile_preview">
    <ProfilePreview />
  </div>
</template>

<script>
import Constants from "controllers/vue_components/mixins/constants"
import {useSettingsStore} from 'controllers/vue_components/settings/v1/store'
import ProfilePreview from 'controllers/vue_components/settings/v1/profile_preview'
import RequestMixin from "controllers/vue_components/mixins/request"

export default {
    name: 'ProfileThemes',
    props: [],
    components: { ProfilePreview },
    mixins: [Constants, RequestMixin],
    data() {
        return {
            settingsStore: useSettingsStore()
        }
    },
    computed: {
        loading() {
            return this.settingsStore.init_data_loading
        },
        error() {
            return this.settingsStore.error
        }
    },
    mounted() {
        this.settingsStore.fetchIfStale()
        this.settingsStore.fetchProfileBannerSettings()
    },
    methods: {
        toggleModal() {
            this.settingsStore.profile_preview = !this.settingsStore.profile_preview
            this.settingsStore.event_embed_url = this.settingsStore.profile_banner_settings?.last_event_embed_url
        },
        retry() {
            this.settingsStore.invalidate()
            this.settingsStore.fetchIfStale()
        }
    },
};
</script>
